'use client';

import { OAuthProvider } from '@modules/auth/providers/OAuthProvider/OAuthProvider';
import { getUTMs } from '@utils/utm';

import { Auth0Provider } from '@auth0/auth0-react';
import React from 'react';

export enum ProvideState {
  AUTH0 = 'AUTH0',
  OIDC = 'OIDC',
  LOADING = 'LOADING',
}

type AuthProviderContextType = {
  authorizedOrganizationId: string | null;
  provider: ProvideState;
  setProvider: (provider: ProvideState) => void;
  setAuthorizedOrganizationId: (organizationId: string) => void;
};

const AuthProviderContext = React.createContext<AuthProviderContextType>(
  {
    authorizedOrganizationId: null,
    provider: ProvideState.LOADING,
    setProvider: () => { throw new Error('Context not initalized'); },
    setAuthorizedOrganizationId: () => { throw new Error('Context not initalized'); },
  },
);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [authorizedOrganizationId, setAuthorizedOrganizationId] = React.useState<string | null>(null);
  const [provider, setProvider] = React.useState<ProvideState>(ProvideState.LOADING);

  const redirectUri = typeof window !== 'undefined' ? `${window.location.origin}/callback` : undefined;

  const utms = getUTMs();

  const context: AuthProviderContextType = React.useMemo(() => ({
    authorizedOrganizationId,
    setAuthorizedOrganizationId,
    provider,
    setProvider,
  }), [authorizedOrganizationId, setAuthorizedOrganizationId, provider, setProvider]);

  return (
    <AuthProviderContext.Provider value={context}>
      {
        provider === 'LOADING' && children
      }

      {
        provider === 'OIDC' && <OAuthProvider>{children}</OAuthProvider>
      }

      {
        provider === 'AUTH0' && (
          <Auth0Provider
            domain={process.env.AUTH_CLIENT_DOMAIN as string}
            clientId={process.env.AUTH_CLIENT_ID as string}
            authorizeTimeoutInSeconds={process.env.AUTH_CLIENT_AUTHORIZE_TIMEOUT_IN_SECONDS as unknown as number}
            useCookiesForTransactions
            legacySameSiteCookie
            cacheLocation='localstorage'
            useRefreshTokens
            useRefreshTokensFallback
            cookieDomain={`.${process.env.SHARED_DOMAIN}`}
            authorizationParams={{
              audience: process.env.AUTH_CLIENT_AUDIENCE_BROWSER,
              redirect_uri: redirectUri,
              organization: authorizedOrganizationId ?? undefined,
              scope: process.env.AUTH_CLIENT_SCOPE,
              ...utms,
            }}
            skipRedirectCallback
          >
            {children}
          </Auth0Provider>
        )
      }
    </AuthProviderContext.Provider>
  );
};

export const useAuthProvider = (): AuthProviderContextType => React.useContext(AuthProviderContext);
